// ============================================================================
// broiler.batchForm.fullscreen — the surface no route row can express
// ============================================================================
// App dispatches it on `showForm && inBroilerSpace && !pathname.startsWith(
// '/broiler/batches/')`, which is why it lives in ROUTE_ORACLE_EXCEPTIONS
// rather than in a view's variants. Being unroutable was never being exempt
// from ownership, and until now it was `legacy.all` like everything else.
//
// IT IS NOT A no-server-read SURFACE, WHICH IS WHAT AN INVENTORY OF ITS
// PHYSICAL CALLS WOULD SUGGEST.
//
// BatchForm.tsx has no PostgREST read of its own: its only `sb.` calls are two
// `sb.storage.from('batch-documents')` uploads and one remove, and its props
// are callbacks and UI flags with no data array among them. On that evidence it
// looks like view.weather's shape. It is not, because the derivation that
// matters here is the provider one: the component read TWO of App's boot values
// off hooks — the complete poultry_dailys history off DailysRecentContext and
// the ppp-v4 batch array off BatchesContext — and a no-server-read declaration
// is an ABSENCE claim. Making it would have been false.
//
// `broiler.batchForm.dailys` owns the first of those now. It is shared with
// broiler.batches.detail because BroilerBatchPage mounts this same component
// `embedded`, and a resource may only name surfaces the manifest declares
// migrated; the two had to move together.
//
// WHY THIS IS PERMANENT NOW, AND WHAT IT TOOK.
//
// `legacy.context.batches.batches` was App's ppp-v4 array, read here as
// `batchRows`. It was the WRITE BASE of the document path:
//
//     const nb = batchRows.map((b) => (b.id === editId ? {...b, documents} : b));
//     setBatches(nb);
//     persist(nb);
//
// `persist` is `sbSave('ppp-v4', nb)` plus a maintenance pass — it replaces the
// whole array. So the base has to be the array as it is NOW, and "now" includes
// every field save `submit` has landed since this form opened. A mount-time
// snapshot would silently revert them. Being subscribed to App's array is what
// makes the base current, and there is no route resource that can be, because
// App owns the writes.
//
// THE THREE SITES WERE EXACTLY THREE, WHICH IS WHY ONE PROP RETIRED ALL OF THEM.
//
// `batchRows` was read at three places in BatchForm.tsx and nowhere else, and
// all three were the same statement — `batchRows.map((b) => (b.id === editId ?
// {...b, documents: newDocs} : b))` followed by `setBatches(nb)` and
// `persist(nb)` — inside the upload, the replace and the remove handlers.
// Nothing rendered from it. So a narrower write really did remove every physical
// read of BatchesContext.batches from this component.
//
// THE WRITE BASE MOVED FIRST (2026-08-19), AND THAT ORDER WAS NOT OPTIONAL.
//
// The second option above was taken, and the catastrophe named in the paragraph
// before it no longer exists: `submit` cannot write a one-element blob, because
// no writer of ppp-v4 supplies its own base any more. App's `persist` reads the
// document at SAVE time through src/lib/broilerBatchesApi.ts and merges the
// caller's array onto it — the caller stays authoritative for every row it
// names, and rows it never named are carried through as the same objects the
// read produced. A row can only be DROPPED by being handed to `removed`, which
// App's `del` is the only caller to do and which the narrower `persist` prop
// type on this component structurally prevents. It fails closed: a failed read,
// a non-array document, or a row carrying neither an id nor a name refuses the
// save outright. That covers all EIGHT writers at once — the three document
// handlers here, App's upd/submit/del, and BroilerListView's two status flips —
// because every one of them goes through `persist`.
//
// It also settles the projection worry broiler.batches.index raises, in the
// narrow case of the WRITE: carrying a stored row through BY REFERENCE cannot
// revert a persisted repair, because writing a row back exactly as it was read
// changes nothing. `applyBroilerBootRepair` is therefore NOT on the save path
// and must not be added to it; it would import a clock dependency and a second
// app_store key, and fire the two live lifecycle rules on rows the operator
// never touched. The projection argument still stands for a route-local READ.
//
// That change removed no physical read, and said so: it left `batchRows` at
// src/broiler/BatchForm.tsx as `useBatches().batches`, still the base of the
// three document handlers, and warned in as many words that emptying this array
// still required the narrow fix and must not be done on the strength of the
// write-base move alone.
//
// THE NARROW FIX IS DONE, AND THE RESIDUAL IS EMPTY (2026-08-19).
//
// `persistDocuments(batchId, documents)` exists in src/App.tsx and is the prop
// this component takes in place of `persist`. The three handlers are one line
// each — `persistDocuments(editId, newDocs)` — and this file destructures
// nothing off BatchesContext but draft state and setters. There is no `batches`
// binding, no `batchRows`, and no `BroilerBatchRow` type left to cast one to.
// The claim this array made is now FALSE, which is the only reason to empty it.
//
// FOUR THINGS THAT MAKE THAT SAFE, AND NOT ONE OF THEM IS "THE READ IS GONE".
//
//   1. THE SAVE NO LONGER DEPENDS ON THE ARRAY FOR CORRECTNESS. The reason the
//      read was defensible was that `persist` replaced the whole blob, so the
//      base had to be current. It does not any more: `persist` merges onto a
//      SAVE-TIME read (src/lib/broilerBatchesApi.ts), and the document patch is
//      applied to that MERGE — so the row is found whether it came from the
//      caller or was carried in from the server.
//
//   2. IT IS STRICTLY BETTER ON THE UNBOOTED MOUNT, NOT MERELY EQUAL. The old
//      expression over an empty `batchRows` was `[]` for any callback and any
//      editId: the file reached the batch-documents bucket and then vanished
//      from the blob in silence. The patch lands on the carried row, and
//      refuses out loud (`documents-unmatched`) when it matches none.
//
//   3. THE SETTER THAT STAYED IS App's, NOT THIS SURFACE'S. `persistDocuments`
//      performs the `setBatches` mirror the three handlers used to perform, in
//      App, over App's own state. The provenance derivation scores a setter as
//      non-consuming, and view.webforms already declares `residualLegacy: []`
//      beside an identical writer. The vocabulary question the paragraph above
//      posed — whether a write coupling belongs under a READ id — is not being
//      answered by assertion here: there is no write coupling left to argue
//      about, because the save is correct with no boot state at all.
//
//   4. THE DRAFT IS NOT A BOOT VALUE. What this component still takes off
//      BatchesContext is `form`, `editId`, `conflicts`, `parsedProcessor`,
//      `showLegacy`, `docUploading` and `formNotice` — App render state filled
//      synchronously by openAdd/openEdit, never by a server read, and named by
//      no residual id in RESIDUAL_LEGACY_IDS.
//
// WHAT THIS DECLARATION PROVES, AND WHAT RELEASE ADDS. The `permanent`
// resource proves data ownership; the ledger row, strict fullscreen host fence,
// boundary target, and renderer branch now release this surface. The sibling
// batch surfaces have their own resource and action-bridge contracts rather
// than inheriting this form's declaration.
//
// Proved by tests/app_broiler_batch_documents_boot_independence.test.js, which
// runs App's real `persistDocuments` and `persist` bodies together against an
// empty boot array and a stocked server, by
// tests/app_broiler_batches_write_base_boot_independence.test.js, and by
// src/lib/broilerBatchesApi.test.ts.
//
// No global-readiness residual is declared. This surface's content is the draft,
// which App fills synchronously from openAdd/openEdit at the moment the form
// opens. This component never reads `dataLoaded`, and the array it does read is
// already named above.
//
// THE STORAGE COMMANDS ARE NOT AN OBLIGATION EITHER WAY. Uploading and removing
// a batch document are user-initiated WRITES to the batch-documents bucket.
// residualLegacy inventories reads; the one-owner rule partitions render
// obligations. Neither is a place for a command, which is the same reading
// view.fuelSupply's receipt applies to its own INSERT.
//
// THE FULLSCREEN ACTION BRIDGE HAS ITS OWN ROUTE-LOCAL BASE (2026-08-20).
//
// BatchForm still does not read the ppp-v4 list — the empty residual remains a
// claim about THIS component file — but its retained App callbacks do. The
// released renderer loads `broiler.batchForm.actionState` and layout-commits
// repaired ppp-v4, normalized feed costs, and layer batches through the three
// existing write mirrors before it mounts those callbacks. That is an
// operational input, not a restored provider READ: it prevents `submit`,
// autosave, and delete from closing over cold empty defaults while preserving
// the callbacks' existing save-time merge, Activity, and conflict behavior.
// The resource is fullscreen-only; the embedded record form remains partial
// for its own BatchesContext reads and does not acquire this bridge.
// ============================================================================

import type {MigratedSurfaceDeclaration} from '../surfaceDataManifest';

const declaration: MigratedSurfaceDeclaration = Object.freeze({
  surface: 'broiler.batchForm.fullscreen',
  obligations: Object.freeze([
    Object.freeze({id: 'broiler.batchForm.actionState', owner: 'permanent'}),
    Object.freeze({id: 'broiler.batchForm.dailys', owner: 'permanent'}),
    Object.freeze({id: 'broiler.timeline.core', owner: 'permanent'}),
  ]),
  residualLegacy: Object.freeze([] as const),
});

export default declaration;
